import { SidebarMenu } from "components";
import { useRef, useState, useEffect } from "react";
import SellersOrders from "../components/SellersOrders";
import CustomersVisitControl from "../components/CustomersVisitControl";
import { formatDateToLocaleDateString, formatDateToISODateString, formatISODateStringToDate, exportToExcel } from "utils";
import axios from "axios";
import { useAppSelector } from "hooks";
import { CircularProgress, Box, Tabs, Tab, Button, Popover, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Checkbox, Divider, Typography } from "@mui/material";

function DateRangeControl({
	systemMin,
	today,
	uiStart,
	setUiStart,
	uiEnd,
	setUiEnd,
	onApply,
}: {
	systemMin: Date;
	today: Date;
	uiStart: Date;
	setUiStart: (d: Date) => void;
	uiEnd: Date;
	setUiEnd: (d: Date) => void;
	onApply: () => void;
}) {
	const MAX_RANGE_DAYS = 44
	const ONE_DAY = 24 * 60 * 60 * 1000;

	const toMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);

	function clampToSystem(date: Date) {
		let x = toMidnight(date);
		const min = toMidnight(systemMin);
		const max = toMidnight(today);
		if (x < min) x = min;
		if (x > max) x = max;
		return x;
	}

	function normalizeWithAnchor(start: Date, end: Date, anchor: "start" | "end") {
		let s = clampToSystem(start);
		let e = clampToSystem(end);

		if (anchor === "start") {
			const maxEnd = new Date(s.getTime() + MAX_RANGE_DAYS * ONE_DAY);
			const cappedMaxEnd = maxEnd > today ? today : maxEnd;
			if (e < s) e = cappedMaxEnd;
			if (e > cappedMaxEnd) e = cappedMaxEnd;
		} else {
			const minStart = new Date(e.getTime() - MAX_RANGE_DAYS * ONE_DAY);
			const cappedMinStart = minStart < systemMin ? systemMin : minStart;
			if (s > e) s = cappedMinStart;
			if (s < cappedMinStart) s = cappedMinStart;
		}

		return [s, e] as const;
	}

	const handleStartInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
		const raw = ev.target.value;
		const picked = formatISODateStringToDate(raw, false);
		if (!picked) return;
		const [s, e] = normalizeWithAnchor(picked, uiEnd, "start");
		setUiStart(s);
		setUiEnd(e);
	};

	const handleEndInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
		const raw = ev.target.value;
		const picked = formatISODateStringToDate(raw, false);
		if (!picked) return;
		const [s, e] = normalizeWithAnchor(uiStart, picked, "end");
		setUiStart(s);
		setUiEnd(e);
	};

	return (
		<div className="flex items-end gap-4">
			<div className="flex flex-col">
				<label className="text-sm font-semibold mb-1">Fecha de inicio</label>
				<input
					type="date"
					className="border rounded-md px-3 py-2"
					min={formatDateToISODateString(systemMin)}
					max={formatDateToISODateString(today)}
					value={formatDateToISODateString(uiStart)}
					onChange={handleStartInput}
				/>
			</div>

			<div className="flex flex-col">
				<label className="text-sm font-semibold mb-1">Fecha de fin</label>
				<input
					type="date"
					className="border rounded-md px-3 py-2"
					min={formatDateToISODateString(systemMin)}
					max={formatDateToISODateString(today)}
					value={formatDateToISODateString(uiEnd)}
					onChange={handleEndInput}
				/>
			</div>

			<button
				className="bg-primary text-white px-4 py-2 rounded-md font-semibold"
				onClick={onApply}
			>
				Aplicar
			</button>
		</div>
	);
}

export const SellersControlPage = () => {
	const { token } = useAppSelector((state) => state.user);
	const today = new Date();
	const systemMin = new Date(2024, 0, 1);

	const [startDateSearchFilter, setStartDateSearchFilter] = useState<Date>(today);
	const [finishDateSearchFilter, setFinishDateSearchFilter] = useState<Date>(new Date());

	const contentRef = useRef<HTMLDivElement | null>(null);
	const [selectedVendors, setSelectedVendors] = useState<string[] | null>(null);
	const [vendorList, setVendorList] = useState<Array<{ code: string; name: string, checked: boolean }>>([]);
	const [loader, setLoader] = useState<boolean>(true);
	const [dataReport, setDataReport] = useState<any>({});
	const [backupDataReport, setBackupDataReport] = useState<any>({});
	const [activeReportTab, setActiveReportTab] = useState<"visits" | "orders">("visits");
	const [vendorAnchorEl, setVendorAnchorEl] = useState<HTMLElement | null>(null);

	useEffect(() => {
		if (token) {
			const controller = new AbortController();
			getDataInitial(controller.signal);
			return () => controller.abort();
		}
	}, []); // eslint-disable-line

	const getDataInitial = async (signal: AbortSignal) => {
		setLoader(true);
		const response = await axios.get(`${process.env.REACT_APP_SERVICE_URL}/documents/get/v2/compliance/range`, {
			params: {
				dayInit: formatDateToISODateString(startDateSearchFilter),
				dayEnd: formatDateToISODateString(finishDateSearchFilter),
			},
			headers: {
				Authorization: `UserToken ${token || ""}`,
			},
			signal,
		});

		const listUser = (response.data?.data?.ordersResume || []).map((item: any) => ({
			code: item.userId,
			name: item.vendorName,
			checked: true,
		}));

		const withAll = [{ code: "all", name: "Todos", checked: true }, ...listUser];

		setVendorList(withAll);
		setDataReport(response.data.data || {})
		setBackupDataReport(response.data.data || {})
		setLoader(false);
	}

	const openVendorPopover = (ev: React.MouseEvent<HTMLElement>) => setVendorAnchorEl(ev.currentTarget);
	const closeVendorPopover = () => setVendorAnchorEl(null);
	const vendorPopoverOpen = Boolean(vendorAnchorEl);

	const toggleVendor = (code: string) => {
		if (code === "all") {
			const allChecked = vendorList.every(v => v.checked);
			const updatedList = vendorList.map(v => ({ ...v, checked: !allChecked }));
			setVendorList(updatedList);
			setSelectedVendors(!allChecked ? [] : null);
			setDataReport(() => !allChecked ? backupDataReport : { details: [], ordersResume: [] });
			return;
		} else {
			const updatedList = vendorList.map(v => {
				if (v.code === code) return { ...v, checked: !v.checked };
				else if (v.code === "all") return { ...v, checked: false };
				return v;
			});
			setVendorList(updatedList);
			const selected = updatedList.filter(v => v.code !== "all" && v.checked).map(v => v.code);
			setSelectedVendors(selected.length === updatedList.length - 1 ? null : selected);
			const filteredData = (Array.isArray(backupDataReport?.ordersResume) ? backupDataReport.ordersResume : []).filter((item: any) => {
				const codeCandidates = [item.userId, item.salesPersonCode, item.vendorCode, item.vendorId].filter(Boolean);
				return selected.includes(String(codeCandidates[0]));
			});
			setDataReport(() => selected.length === updatedList.length - 1 ? backupDataReport : { details: filteredData, ordersResume: filteredData });
			return;
		}
	};

	const handleExportExcel = () => {
		let payload: any[] = [];
		if (activeReportTab === "visits") payload = Array.isArray(dataReport?.ordersResume) ? dataReport.ordersResume : [];
		else payload = Array.isArray(dataReport?.details) ? dataReport.details : [];
		exportToExcel(payload, activeReportTab === "visits" ? "efectividad_visitas" : "ordenes_vendedores", activeReportTab === "visits" ? "Visitas" : "Órdenes");
	};

	return (
		<div ref={contentRef}>
			<div className="w-full h-screen grid grid-cols-12">
				<div className="col-span-2 border-r-2 border-light">
					<SidebarMenu pageName="sellers_control" />
				</div>

				<div className="w-full h-screen flex flex-col flex-grow col-span-10">
					<div className="w-full flex justify-between items-center border-b-2 border-tertiary px-2 py-3">
						<div className="flex flex-col items-start gap-2">
							<p className="font-semibold text-lg">
								Del {formatDateToLocaleDateString(startDateSearchFilter)} al {formatDateToLocaleDateString(finishDateSearchFilter)}
							</p>
						</div>

						<div className="flex items-center gap-6">
							<DateRangeControl
								systemMin={systemMin}
								today={today}
								uiStart={startDateSearchFilter}
								setUiStart={setStartDateSearchFilter}
								uiEnd={finishDateSearchFilter}
								setUiEnd={setFinishDateSearchFilter}
								onApply={() => {
									const controller = new AbortController();
									getDataInitial(controller.signal);
								}}
							/>

							<div>
								<Button variant="outlined" onClick={openVendorPopover}>
									Vendedores {selectedVendors === null ? "(Todos)" : `(${selectedVendors.length})`}
								</Button>
								<Popover
									open={vendorPopoverOpen}
									anchorEl={vendorAnchorEl}
									onClose={closeVendorPopover}
									anchorOrigin={{
										vertical: 'bottom',
										horizontal: 'left',
									}}
								>
									<div style={{ width: 390, maxHeight: 360, overflow: 'auto' }}>
										<List dense>
											<ListItem key="label">
												<ListItemText>
													<Typography variant="subtitle1">Filtrar por vendedor</Typography>
												</ListItemText>
											</ListItem>
											<Divider />
											{vendorList.map((v) => (
												<ListItemButton key={v.code} onClick={() => toggleVendor(v.code)}>
													<ListItemIcon>
														<Checkbox
															edge="start"
															checked={v.checked}
															tabIndex={-1}
															disableRipple
															inputProps={{ 'aria-labelledby': `checkbox-list-label-${v.code}` }}
														/>
													</ListItemIcon>
													<ListItemText id={`checkbox-list-label-${v.code}`} primary={v.name} secondary={v.code !== "all" ? v.code : undefined} />
												</ListItemButton>
											))}
											<Divider />
										</List>
									</div>
								</Popover>
							</div>

							<button onClick={handleExportExcel} className="bg-primary text-white px-4 py-2 mt-6 rounded">
								Descargar Excel
							</button>
						</div>
					</div>

					<Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "white" }}>
						<Tabs
							value={activeReportTab}
							onChange={(_, v) => setActiveReportTab(v)}
							indicatorColor="primary"
							textColor="primary"
						>
							<Tab value="visits" label="Efectividad de visitas" />
							<Tab value="orders" label="Órdenes de vendedores" />
						</Tabs>
					</Box>

					<div style={{ flex: 1, overflow: "auto" }}>
						{loader ? (
							<div className="p-6 flex justify-center">
								<CircularProgress />
							</div>
						) : activeReportTab === "visits" ? (
							<CustomersVisitControl dataReport={dataReport.ordersResume || []} />
						) : (
							<SellersOrders dataReport={dataReport?.details || []} />
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
